import { Box, Text, Button, Flex, Badge, HStack, Icon } from '@chakra-ui/react';
import {
    CheckCircleIcon, WarningIcon, InfoIcon, TimeIcon, CloseIcon
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import * as routesNames from '../../../../constants/routes';
import { ResignationNotification } from '../../../../hooks/useResignationNotification';

interface Props {
    notification: ResignationNotification;
    onDismiss: () => void;
}

const typeConfig: Record<string, {
    icon: any;
    iconColor: string;
    accentColor: string;
    badgeScheme: string;
    badgeLabel: string;
    badgeBg: string;
}> = {
    new_resignation: {
        icon: InfoIcon, iconColor: '#2563eb', accentColor: '#2563eb',
        badgeScheme: 'blue', badgeLabel: '📋 New Resignation', badgeBg: '#eff6ff'
    },
    withdrawal_request: {
        icon: WarningIcon, iconColor: '#d97706', accentColor: '#d97706',
        badgeScheme: 'orange', badgeLabel: '↩️ Withdrawal', badgeBg: '#fffbeb'
    },
    buyout_request: {
        icon: TimeIcon, iconColor: '#7c3aed', accentColor: '#7c3aed',
        badgeScheme: 'purple', badgeLabel: '⏰ Buyout Request', badgeBg: '#f5f3ff'
    },
    buyout_pending_accounts: {
        icon: TimeIcon, iconColor: '#7c3aed', accentColor: '#7c3aed',
        badgeScheme: 'purple', badgeLabel: '💰 Buyout Pending', badgeBg: '#f5f3ff'
    },
    buyout_approved: {
        icon: CheckCircleIcon, iconColor: '#059669', accentColor: '#006838',
        badgeScheme: 'green', badgeLabel: '✅ Buyout Approved', badgeBg: '#ecfdf5'
    },
    buyout_rejected: {
        icon: CloseIcon, iconColor: '#dc2626', accentColor: '#dc2626',
        badgeScheme: 'red', badgeLabel: '❌ Buyout Rejected', badgeBg: '#fef2f2'
    },
    withdrawal_approved: {
        icon: CheckCircleIcon, iconColor: '#059669', accentColor: '#006838',
        badgeScheme: 'green', badgeLabel: '✅ Withdrawal Approved', badgeBg: '#ecfdf5'
    },
    withdrawal_rejected: {
        icon: CloseIcon, iconColor: '#dc2626', accentColor: '#dc2626',
        badgeScheme: 'red', badgeLabel: '❌ Withdrawal Rejected', badgeBg: '#fef2f2'
    },
};

const getNavigateTo = (type: string) => {
    const hrTypes = ['new_resignation', 'withdrawal_request', 'buyout_request'];
    const accountsTypes = ['buyout_pending_accounts'];
    if (hrTypes.includes(type)) return routesNames.RESIGNATIONAPPROVAL;
    if (accountsTypes.includes(type)) return '/buyout-processing';
    return routesNames.RESIGNATION;
};

const ResignationNotificationPopup = ({ notification, onDismiss }: Props) => {
    const navigate = useNavigate();
    const cfg = typeConfig[notification.type] ?? typeConfig['new_resignation'];

    return (
        <Box
            position="fixed"
            bottom="160px"
            right="24px"
            zIndex={9998}
            bg="white"
            borderRadius="14px"
            boxShadow="0 8px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)"
            p={4}
            w="340px"
            borderLeft={`4px solid ${cfg.accentColor}`}
            animation="resignSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)"
            sx={{
                '@keyframes resignSlideIn': {
                    from: { transform: 'translateX(120%)', opacity: 0 },
                    to: { transform: 'translateX(0)', opacity: 1 }
                }
            }}
        >
            {/* Header */}
            <Flex justify="space-between" align="flex-start" mb={2.5}>
                <HStack spacing={2}>
                    <Icon as={cfg.icon} color={cfg.iconColor} boxSize={4} />
                    <Text fontWeight="700" fontSize="xs" color="gray.700" textTransform="uppercase" letterSpacing="0.5px">
                        Resignation Update
                    </Text>
                </HStack>
                <Button
                    size="xs" variant="ghost" onClick={onDismiss}
                    color="gray.400" _hover={{ color: 'gray.600', bg: 'gray.100' }}
                    minW="auto" p={1} borderRadius="full"
                >
                    ✕
                </Button>
            </Flex>

            {/* Badge */}
            <Box mb={2}>
                <Badge
                    px={2.5} py={1} borderRadius="full" fontSize="10px" fontWeight="bold"
                    bg={cfg.badgeBg} color={cfg.iconColor}
                >
                    {cfg.badgeLabel}
                </Badge>
            </Box>

            {/* Message */}
            <Text fontWeight="600" fontSize="sm" mb={1} color="gray.800" noOfLines={2}>
                {notification.message}
            </Text>
            <Text fontSize="xs" color="gray.500" mb={notification.amount ? 2 : 3}>
                {notification.detail}
            </Text>

            {/* Amount (if buyout approved) */}
            {notification.amount != null && (
                <Box mb={3} px={3} py={2} bg="#ecfdf5" borderRadius="md" border="1px solid" borderColor="#bbf7d0">
                    <Text fontSize="xs" color="#059669" fontWeight="medium">Buyout Amount</Text>
                    <Text fontSize="md" fontWeight="bold" color="#006838">
                        ₹{Number(notification.amount).toLocaleString('en-IN')}
                    </Text>
                </Box>
            )}

            {/* Actions */}
            <Flex gap={2}>
                <Button
                    size="sm"
                    bg={cfg.accentColor}
                    color="white"
                    flex={1}
                    borderRadius="full"
                    _hover={{ opacity: 0.9 }}
                    fontSize="xs"
                    onClick={() => {
                        navigate(getNavigateTo(notification.type));
                        onDismiss();
                    }}
                >
                    View Details
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    flex={1}
                    borderRadius="full"
                    fontSize="xs"
                    color="gray.500"
                    onClick={onDismiss}
                >
                    Dismiss
                </Button>
            </Flex>
        </Box>
    );
};

export default ResignationNotificationPopup;
